import { useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { format } from 'date-fns';
import { parseDateOnly } from '@/utils/dateUtils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { DecimalInput } from '@/components/common/DecimalInput';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import {
  useCreateCashReceipt,
  useUpdateCashReceipt,
} from '@/api/rpc/hooks/accounts-receivable/useArCashReceipts';
import { useToast } from 'src/context/ToastContext';
import type { CashReceiptDetail } from '@erp/rpc';

interface CashReceiptFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Provided in edit mode; omit for create. */
  receipt?: CashReceiptDetail;
  onSaved?: (receiptId: string) => void;
}

export function CashReceiptFormDialog({
  open,
  onOpenChange,
  receipt,
  onSaved,
}: CashReceiptFormDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const createMutation = useCreateCashReceipt();
  const updateMutation = useUpdateCashReceipt();
  const isEdit = Boolean(receipt);
  const isPending = createMutation.isPending || updateMutation.isPending;

  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [amount, setAmount] = useState<string | null>(null);
  const [transactionDate, setTransactionDate] = useState<string | null>(null);
  const [merchantName, setMerchantName] = useState('');
  const [paymentChannel, setPaymentChannel] = useState('');

  // Re-seed only on open or when targeting a different receipt; keyed on id
  // via a ref so a background refetch can't wipe in-progress edits.
  const initedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      initedKey.current = null;
      return;
    }
    const key = receipt?.id ?? '__new__';
    if (initedKey.current === key) return;
    initedKey.current = key;
    setCustomerId(receipt?.customerId ?? undefined);
    setAmount(receipt?.paymentAmount ?? null);
    setTransactionDate(receipt?.paymentDate ?? null);
    setMerchantName(receipt?.bankTransaction?.merchantName ?? '');
    setPaymentChannel(receipt?.bankTransaction?.type ?? '');
  }, [open, receipt]);

  const canSubmit = amount !== null && amount !== '' && !isPending;

  async function handleSubmit() {
    if (!canSubmit || amount === null) return;
    try {
      const result =
        isEdit && receipt
          ? await updateMutation.mutateAsync({
              id: receipt.id,
              amount,
              transactionDate,
              merchantName: merchantName.trim() || null,
              paymentChannel: paymentChannel.trim() || null,
            })
          : await createMutation.mutateAsync({
              amount,
              transactionDate,
              merchantName: merchantName.trim() || null,
              paymentChannel: paymentChannel.trim() || null,
              customerId: customerId ?? null,
            });
      showToast({
        title: isEdit
          ? gt('Cash receipt updated', {
              context: 'toast title, cash receipt updated',
            })
          : gt('Cash receipt created', {
              context: 'toast title, cash receipt created',
            }),
        color: 'success',
        icon: 'check',
      });
      onSaved?.(result.receipt.id);
      onOpenChange(false);
    } catch (err) {
      showToast({
        title: gt('Save failed', {
          context: 'toast title, cash receipt save failed',
        }),
        message: err instanceof Error ? err.message : '',
        color: 'error',
        icon: 'warning',
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? (
              <T context="dialog title, edit a cash receipt">
                Edit cash receipt
              </T>
            ) : (
              <T context="dialog title, record a new cash receipt">
                Record a cash receipt
              </T>
            )}
          </DialogTitle>
          <DialogDescription>
            <T context="dialog description, manual cash receipt entry">
              Manually record a bank deposit. It lands in the review queue where
              you can match it to a customer and payment.
            </T>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {!isEdit && (
            <div className="space-y-2">
              <Label>
                <T context="form label, cash receipt customer">
                  Customer (optional)
                </T>
              </Label>
              <CustomersAutocomplete
                selectedCustomerId={customerId}
                onCustomerSelect={setCustomerId}
                allowClear
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cr-amount">
                <T context="form label, cash receipt amount">Amount</T>
              </Label>
              <DecimalInput
                id="cr-amount"
                value={amount}
                onChange={setAmount}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cr-date">
                <T context="form label, cash receipt date">Date</T>
              </Label>
              <DatePicker
                value={
                  transactionDate
                    ? (parseDateOnly(transactionDate) ?? undefined)
                    : undefined
                }
                onChange={(d) =>
                  setTransactionDate(d ? format(d, 'yyyy-MM-dd') : null)
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="cr-merchant">
                <T context="form label, cash receipt merchant / payer">
                  Payer / merchant
                </T>
              </Label>
              <Input
                id="cr-merchant"
                value={merchantName}
                onChange={(e) => setMerchantName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cr-channel">
                <T context="form label, cash receipt payment channel">
                  Channel
                </T>
              </Label>
              <Input
                id="cr-channel"
                value={paymentChannel}
                onChange={(e) => setPaymentChannel(e.target.value)}
                placeholder={gt('ach, wire, check...', {
                  context: 'placeholder, payment channel',
                })}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            <T context="button label, cancel dialog">Cancel</T>
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isEdit ? (
              <T context="button label, save cash receipt edits">Save</T>
            ) : (
              <T context="button label, create cash receipt">Create</T>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
